import { PropsWithChildren, useMemo } from 'react';
import { css } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { AnalyticsHooks, useIsAutoRecallAnalyticsDemoAccount } from '@frontend/analytics-shared';
import { FeatureLocked } from '@frontend/empty-states#svg';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { useIsPADemoAccount } from '../components/practice/hooks/use-is-pa-demo-account';

type Feature =
  | 'appointment'
  | 'auto-recall'
  | 'dashboard'
  | 'messaging'
  | 'phone'
  | 'practice'
  | 'reviews'
  | 'ai-receptionist';

interface AccessPageProps extends PropsWithChildren {
  isAllAccessScopeEnabled?: boolean;
  demoFeatures?: {
    [key in Feature]?: boolean;
  };
  feature: Feature;
  title: string;
}

export const AnalyticsAccessPage = ({
  isAllAccessScopeEnabled,
  children,
  demoFeatures,
  feature,
  title,
}: AccessPageProps) => {
  const { t } = useTranslation('analytics');
  const isAutoRecallAnalyticsDemoAccount = useIsAutoRecallAnalyticsDemoAccount();
  const isPracticeAnalyticsDemoAccount = useIsPADemoAccount();

  const hasDemoDataAccess = useMemo(() => {
    switch (feature) {
      case 'dashboard':
        return (
          demoFeatures?.phone ||
          demoFeatures?.messaging ||
          isAutoRecallAnalyticsDemoAccount ||
          isPracticeAnalyticsDemoAccount
        );

      case 'practice':
        return isPracticeAnalyticsDemoAccount;

      case 'auto-recall':
        return isAutoRecallAnalyticsDemoAccount;

      case 'phone':
        return demoFeatures?.phone;

      case 'messaging':
        return demoFeatures?.messaging;

      case 'appointment':
        return demoFeatures?.appointment;

      case 'ai-receptionist':
        return demoFeatures?.['ai-receptionist'];

      default:
        return false;
    }
  }, [demoFeatures, isAutoRecallAnalyticsDemoAccount, isPracticeAnalyticsDemoAccount, feature]);

  const { someLocationsHaveAclAccess } = AnalyticsHooks.useHasAclAccess({
    isAllAccessScopeEnabled,
    isDemoAccount: hasDemoDataAccess,
    permissions: [Permission.ANALYTICS_READ],
  });

  if (someLocationsHaveAclAccess) {
    return children;
  } else {
    return (
      <Page maxWidth='100%'>
        <Page.Header>
          <Page.Header.Heading>
            <Page.Header.Title title={title} />
          </Page.Header.Heading>
        </Page.Header>

        <Page.Body>
          <div css={styles.noAccessWrapper}>
            <FeatureLocked style={{ maxWidth: 400, marginBottom: theme.spacing(5) }} />
            <Text color='subdued' textAlign='center'>
              {t('You do not have permission to view {{feature}}.', { feature: title })}
            </Text>
            <Text color='subdued' textAlign='center'>
              {t('Please contact your admin to upgrade your access.')}
            </Text>
          </div>
        </Page.Body>
      </Page>
    );
  }
};

const styles = {
  noAccessWrapper: css`
    align-items: center;
    display: flex;
    flex-direction: column;
    min-height: 100%;
    padding-top: ${theme.spacing(8)};
  `,
};
